import type { ComponentType, ReactNode } from 'react'
import type { IPublicClientApplication } from '@azure/msal-browser'
import { ApiError, configureAuth } from '@/api/client'
import type { AuthConfig } from '@/api/types'

export type AuthSession =
  | { mode: 'none' }
  | { mode: 'local' }
  | { mode: 'redirecting' }
  | {
      mode: 'entra'
      instance: IPublicClientApplication
      Provider: ComponentType<{ instance: IPublicClientApplication; children?: ReactNode }>
      accountName: string | null
      signOut: () => void
    }

/**
 * Entra ID sign-in. MSAL is loaded lazily so `AUTH_MODE=none` deployments never
 * download it. Flow: handle a returning redirect → pick the signed-in account →
 * otherwise start loginRedirect. Tokens for `api_scope` are acquired silently per
 * request; if interaction is required we fall back to a redirect.
 */
export async function initAuth(auth: AuthConfig): Promise<AuthSession> {
  // Local accounts use an HttpOnly session cookie: nothing to set up in the browser.
  if (auth.mode === 'local') return { mode: 'local' }
  if (auth.mode !== 'entra') return { mode: 'none' }

  const [msal, msalReact] = await Promise.all([import('@azure/msal-browser'), import('@azure/msal-react')])
  const scopes = [auth.api_scope]
  const origin = window.location.origin

  const instance = new msal.PublicClientApplication({
    auth: {
      clientId: auth.client_id,
      authority: `https://login.microsoftonline.com/${auth.tenant_id}`,
      redirectUri: origin,
      postLogoutRedirectUri: origin,
    },
    cache: { cacheLocation: msal.BrowserCacheLocation.SessionStorage },
  })
  await instance.initialize()

  const result = await instance.handleRedirectPromise()
  const account = result?.account ?? instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null

  let redirecting = false
  const login = () => {
    if (redirecting) return
    redirecting = true
    instance.loginRedirect({ scopes, redirectStartPage: window.location.href }).catch(() => {
      redirecting = false
    })
  }

  if (!account) {
    login()
    return { mode: 'redirecting' }
  }
  instance.setActiveAccount(account)

  configureAuth({
    getToken: async () => {
      const active = instance.getActiveAccount()
      if (!active) {
        login()
        throw new ApiError(401, 'Signing you in…')
      }
      try {
        const token = await instance.acquireTokenSilent({ scopes, account: active })
        return token.accessToken
      } catch (error) {
        if (error instanceof msal.InteractionRequiredAuthError && !redirecting) {
          redirecting = true
          await instance.acquireTokenRedirect({
            scopes,
            account: active,
            redirectStartPage: window.location.href,
          })
        }
        throw new ApiError(401, 'Your session has expired. Signing you in again…')
      }
    },
    onUnauthorized: login,
  })

  return {
    mode: 'entra',
    instance,
    Provider: msalReact.MsalProvider,
    accountName: account.name ?? account.username ?? null,
    signOut: () => {
      void instance.logoutRedirect({ account: instance.getActiveAccount() ?? account })
    },
  }
}
